"use client";

import { useSyncExternalStore } from "react";

type NetworkFailure = { id: number; uncertain: boolean };
let failure: NetworkFailure | null = null;
let sequence = 0;
const failureListeners = new Set<() => void>();
export function reportNetworkFailure(uncertain: boolean) {
  failure = { id: ++sequence, uncertain };
  failureListeners.forEach((notify) => notify());
}
export function dismissNetworkFailure() {
  failure = null;
  failureListeners.forEach((notify) => notify());
}
const subscribeFailure = (notify: () => void) => {
  failureListeners.add(notify);
  return () => { failureListeners.delete(notify); };
};
export const useNetworkFailure = () => useSyncExternalStore(subscribeFailure, () => failure, () => null);

// KAN-1606/1609: признаки сети в браузере — для подсказок «нет сети», «слабая сеть», «возможно, мешает VPN».
// Скорость сети сообщают только Chromium-браузеры (Network Information API); Safari и Firefox — нет.
// VPN браузер не видит вовсе: о нём говорим «возможно», когда сеть есть, а сервер не отвечает.

interface NetworkInformation {
  effectiveType?: string;
  rtt?: number;
}

export const isSlowNetwork = () => {
  if (typeof navigator === "undefined") return false;
  const c = (navigator as Navigator & { connection?: NetworkInformation })
    .connection;
  return (
    !!c &&
    (c.effectiveType === "slow-2g" ||
      c.effectiveType === "2g" ||
      (c.rtt ?? 0) > 800)
  );
};

const subscribeOnline = (cb: () => void) => {
  window.addEventListener("online", cb);
  window.addEventListener("offline", cb);
  return () => {
    window.removeEventListener("online", cb);
    window.removeEventListener("offline", cb);
  };
};

/** Есть ли у браузера сеть; при отрисовке на сервере — «есть» (без расхождения при гидратации). */
export const useOnline = () =>
  useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );

const noSubscribe = () => () => {};

/** Медленная ли сеть по данным браузера; на сервере — нет. */
export const useSlowNetwork = () =>
  useSyncExternalStore(noSubscribe, isSlowNetwork, () => false);
